import { Freshness } from './Freshness';
import {
  FD_TRIP_PROGRESS_STAGES,
  fdTripProgressIndex,
  type ZohoFdDealCommercialContext,
} from '@pazportal/types';
import { StatusBadge } from './StatusBadge';
import { detectZohoFieldTripConflicts } from '../lib/zoho-deal-conflicts';
import { useZohoFdDeal, zohoDealFreshnessProps } from '../hooks/useZohoDeal';
import { formatDate, daysBetween } from '../lib/formatters';
import { useI18n } from '../i18n/I18nProvider';

/**
 * Customer-safe trip shape: no lead technician or other staff identity
 * (rule: staff identities never reach the customer surface).
 */
type FieldTrip = {
  id: string;
  project_number: string;
  scope: string | null;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
};

interface ActiveFieldProjectCardProps {
  trip: FieldTrip;
}

function ScopeTagRow({ zoho }: { zoho: ZohoFdDealCommercialContext }) {
  const tags = [
    ...zoho.scopeTags.deploymentCategory,
    zoho.scopeTags.deploymentIntent,
    zoho.scopeTags.primaryDeploymentSector,
    zoho.scopeTags.hotelSegment,
    zoho.scopeTags.tripComplexity,
  ].filter((t): t is string => Boolean(t));

  if (tags.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((tag) => (
        <span
          key={tag}
          className="rounded-full bg-surface-3 px-2 py-0.5 text-[10px] text-text-muted"
        >
          {tag}
        </span>
      ))}
    </div>
  );
}

/** Crew size only — engineer names are internal staff identity. */
function CrewSize({
  engineers,
}: {
  engineers: ZohoFdDealCommercialContext['engineersAssigned'];
}) {
  const { t } = useI18n();
  if (engineers.length === 0) return null;
  return (
    <p className="text-xs text-text-muted">
      {engineers.length === 1
        ? t('trip.engineer_assigned_one')
        : t('trip.engineers_assigned', { n: engineers.length })}
    </p>
  );
}

function FdTripProgressStrip({ salesStage }: { salesStage: string | null }) {
  const { t } = useI18n();
  const activeIndex = fdTripProgressIndex(salesStage);
  const notStarted = activeIndex < 0;
  const current = notStarted ? null : FD_TRIP_PROGRESS_STAGES[activeIndex];

  return (
    <div className="mt-2 space-y-1">
      {/* The stage in words, because ten labels across a phone-width card are
          about thirty pixels each — a width no label survives. The bar carries
          the shape of the progress; this line carries its meaning. */}
      <p className="text-[11px] text-text-muted">
        {notStarted
          ? t('trip.progress_not_started')
          : t('trip.progress_step', {
              n: activeIndex + 1,
              total: FD_TRIP_PROGRESS_STAGES.length,
              stage: current?.short ?? '',
            })}
      </p>
      <div
        className="flex gap-0.5"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={FD_TRIP_PROGRESS_STAGES.length}
        aria-valuenow={notStarted ? 0 : activeIndex + 1}
        aria-valuetext={
          notStarted
            ? t('trip.progress_not_started')
            : t('trip.progress_step', {
                n: activeIndex + 1,
                total: FD_TRIP_PROGRESS_STAGES.length,
                stage: current?.short ?? '',
              })
        }
      >
        {FD_TRIP_PROGRESS_STAGES.map((stage, i) => {
          const isActive = !notStarted && i <= activeIndex;
          return (
            <div key={stage.zoho} className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
              <div
                title={stage.zoho}
                className={`h-1.5 w-full rounded-full ${isActive ? 'bg-success' : 'bg-surface-3'}`}
              />
              {/* Labels return only where there is room for them. */}
              <span className="hidden w-full truncate text-center text-[11px] text-text-faint lg:block">
                {stage.short}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RelatedTripsStrip({
  trips,
  currentDealId,
}: {
  trips: ZohoFdDealCommercialContext['relatedTrips'];
  currentDealId: string;
}) {
  const { t } = useI18n();
  // `t` shadows nothing here: the filter's parameter is renamed to avoid it.
  const siblings = trips.filter((rt) => rt.dealId !== currentDealId || trips.length > 1);
  if (siblings.length <= 1 && trips.length <= 1) return null;

  return (
    <div className="mt-3 border-t border-border/60 pt-2 space-y-1">
      <p className="text-[11px] uppercase tracking-wide text-text-faint">{t('trip.related')}</p>
      {trips.map((rt) => (
        <div
          key={rt.dealId}
          className={`flex justify-between gap-2 text-xs ${
            rt.isCurrent ? 'text-text font-medium' : 'text-text-muted'
          }`}
        >
          <span className="truncate">{rt.dealName ?? 'Unnamed trip'}</span>
          <span className="flex-shrink-0">
            {rt.portalStatus ?? rt.salesStage ?? '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Active Field Project — Supabase field_trips are authoritative (§1).
 * Zoho FD Deal (§C) is reflective commercial context only.
 */
export function ActiveFieldProjectCard({ trip }: ActiveFieldProjectCardProps) {
  const { t } = useI18n();
  const { data: zohoRead, isError: zohoError } = useZohoFdDeal(trip.id);
  const zoho = zohoRead?.deal ?? null;
  const conflicts = zoho
    ? detectZohoFieldTripConflicts({ ...trip, lead_technician: null }, zoho, trip.id)
    : [];
  const conflictFields = new Set(conflicts.map((c) => c.field));

  const tripDay = trip.start_date
    ? daysBetween(trip.start_date, new Date().toISOString()) + 1
    : 0;

  return (
    <div className="rounded-lg border border-border bg-surface p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-medium text-text-muted">Active Field Project</h3>
        {zoho && <Freshness {...zohoDealFreshnessProps(zoho.freshness)} />}
        {zohoError && (
          <span className="text-xs text-warning" role="status">
            CRM context unavailable
          </span>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-text truncate">{trip.project_number}</p>
          </div>
          <StatusBadge status={trip.status ?? 'scheduled'} />
        </div>

        <p className="text-sm text-text-muted">{trip.scope}</p>

        <div className="flex flex-wrap justify-between gap-2 text-xs text-text-muted">
          <span>
            {formatDate(trip.start_date)} — {formatDate(trip.end_date)}
          </span>
          {trip.start_date && (
            <span className="font-medium">{t('trip.day', { n: Math.max(1, tripDay) })}</span>
          )}
        </div>

        {zoho && (
          <div className="mt-3 rounded-md border border-border/60 bg-surface-2/50 px-3 py-2 space-y-2">
            <p className="text-[10px] uppercase tracking-wide text-text-faint">
              CRM context (reflective)
            </p>

            <div className="flex flex-wrap gap-2">
              {zoho.portalStatus && (
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  {zoho.portalStatus}
                </span>
              )}
              {zoho.salesStage && (
                <span className="rounded-full bg-surface-3 px-2 py-0.5 text-xs text-text-muted">
                  {t('trip.sales_stage', { stage: zoho.salesStage })}
                </span>
              )}
              {conflictFields.has('portal_status') && (
                <span className="text-xs text-warning">· differs from portal record</span>
              )}
            </div>

            <ScopeTagRow zoho={zoho} />

            {zoho.accountName && (
              <p className="text-xs text-text-muted truncate">Account: {zoho.accountName}</p>
            )}

            {(zoho.tripStartDate || zoho.tripEndDate) && (
              <p
                className={`text-xs ${
                  conflictFields.has('start_date') || conflictFields.has('end_date')
                    ? 'text-warning'
                    : 'text-text-muted'
                }`}
              >
                Trip dates (CRM): {formatDate(zoho.tripStartDate)} — {formatDate(zoho.tripEndDate)}
              </p>
            )}

            {zoho.salesCloseDate && (
              <p className="text-xs text-text-faint">Sales close: {formatDate(zoho.salesCloseDate)}</p>
            )}

            <CrewSize engineers={zoho.engineersAssigned} />

            {zoho.description && (
              <p className="text-xs text-text-faint border-t border-border/40 pt-1">
                {zoho.description}
              </p>
            )}

            <FdTripProgressStrip salesStage={zoho.salesStage} />

            <RelatedTripsStrip trips={zoho.relatedTrips} currentDealId={zoho.dealId} />
          </div>
        )}
      </div>
    </div>
  );
}
